import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import Header from "../components/layout/Header";
import { useNavigate } from "react-router-dom";

import {
    getEmployerJobs,
    createJobPosting,
    publishJob,
    closeJob,
    archiveJob,
    updateJobPosting,
    getJobDetails,
} from "../services/jobAPI"; // same as your dashboard usage [file:4]

import EditJobModal from "../components/job-listing/EditJobModal"; // reuse existing [file:12]
import ViewJobModal from "../components/job-listing/ViewJobModal"; // ✅ add this

import "./styles/JobPostingManagement.css";

const initialForm = {
    title: "",
    department: "",
    location: "",
    locationType: "ONSITE",
    type: "FULL_TIME",
    openings: 1,
    description: "",
    keyResponsibilities: "",
    validityDays: 30,
};

const initialRequirements = {
    education: { minimum: "", preferred: "" },
    experience: { minimumYears: 0, preferredYears: 0 },
    certifications: { mandatory: [], preferred: [] },
    skills: { mandatory: [], preferred: [] },
    other: "",
};

const JobPostingManagement = () => {
    const { token, isEmployer } = useAuth();
    const navigate = useNavigate();

    const [tab, setTab] = useState("manage"); // "manage" | "create"

    // Jobs state
    const [loadingJobs, setLoadingJobs] = useState(true);
    const [jobs, setJobs] = useState([]);
    const [jobError, setJobError] = useState("");
    const [jobSuccess, setJobSuccess] = useState("");

    // Manage UI state
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("ALL");
    const [departmentFilter, setDepartmentFilter] = useState("ALL");

    // Edit modal
    const [editJob, setEditJob] = useState(null);

    // view job modal
    const [viewOpen, setViewOpen] = useState(false);
    const [viewJob, setViewJob] = useState(null);
    const [viewLoading, setViewLoading] = useState(false);


    // Create wizard state
    const [step, setStep] = useState(1); // 1..3
    const [form, setForm] = useState(initialForm);
    const [requirements, setRequirements] = useState(initialRequirements);
    const [publishingOptions, setPublishingOptions] = useState({
        internalPortal: true,
        publicBoard: true,
    });
    const [creating, setCreating] = useState(false);

    const loadJobs = async () => {
        try {
            setLoadingJobs(true);
            const res = await getEmployerJobs(token); // [file:4]
            setJobs(res.data || []);
            setJobError("");
        } catch (e) {
            setJobs([]);
            setJobError("Failed to load jobs");
        } finally {
            setLoadingJobs(false);
        }
    };

    useEffect(() => {
        if (!isEmployer) return;
        loadJobs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, isEmployer]);

    useEffect(() => {
        if (!jobSuccess) return;
        const t = setTimeout(() => setJobSuccess(""), 4500);
        return () => clearTimeout(t);
    }, [jobSuccess]);

    const stats = useMemo(() => {
        const total = jobs.length;
        const active = jobs.filter(j => j.status === "PUBLISHED").length;
        const closed = jobs.filter(j => j.status === "CLOSED").length;
        const expired = jobs.filter(j => j.status === "EXPIRED").length;
        return { total, active, closed, expired };
    }, [jobs]);

    const departments = useMemo(() => {
        const set = new Set(jobs.map(j => j.department).filter(Boolean));
        return ["ALL", ...Array.from(set)];
    }, [jobs]);

    const filteredJobs = useMemo(() => {
        return jobs
            .filter(j => {
                const q = search.trim().toLowerCase();
                if (!q) return true;
                return (
                    (j.title || "").toLowerCase().includes(q) ||
                    (j.department || "").toLowerCase().includes(q)
                );
            })
            .filter(j => (statusFilter === "ALL" ? true : j.status === statusFilter))
            .filter(j =>
                departmentFilter === "ALL" ? true : j.department === departmentFilter
            );
    }, [jobs, search, statusFilter, departmentFilter]);

    // Actions
    const handleEditSave = async (updatedData) => {
        const jobId = editJob?._id || editJob?.id;
        if (!jobId) {
            setJobError("No job ID found for update!");
            return;
        }
        try {
            await updateJobPosting(jobId, updatedData, token); // [file:4]
            setJobSuccess("Job updated!");
            setEditJob(null);
            await loadJobs();
        } catch (e) {
            setJobError("Failed to update job");
        }
    };

    const handlePublish = async (jobId) => {
        try {
            await publishJob(jobId, token); // [file:4]
            await loadJobs();
            setJobSuccess("Job published!");
        } catch (e) {
            setJobError("Failed to publish job");
        }
    };

    const handleClose = async (jobId) => {
        try {
            await closeJob(jobId, token); // [file:4]
            await loadJobs();
            setJobSuccess("Job closed!");
        } catch (e) {
            setJobError("Failed to close job");
        }
    };

    const handleArchive = async (jobId) => {
        try {
            await archiveJob(jobId, token); // [file:4]
            await loadJobs();
            setJobSuccess("Job archived!");
        } catch (e) {
            setJobError("Failed to archive job");
        }
    };

    const handleViewJob = async (jobId) => {
        try {
            setViewOpen(true);
            setViewLoading(true);
            setViewJob(null);

            const res = await getJobDetails(jobId, token);
            setViewJob(res.data);
        } catch (e) {
            setJobError("Failed to load job details");
            setViewOpen(false);
        } finally {
            setViewLoading(false);
        }
    };


    // Wizard handlers
    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleReqChange = (section, key, value) => {
        setRequirements(prev => ({
            ...prev,
            [section]: { ...prev[section], [key]: value },
        }));
    };

    const handleReqArrChange = (section, key, value) => {
        const arr = value
            .split(",")
            .map(s => s.trim())
            .filter(Boolean);
        setRequirements(prev => ({
            ...prev,
            [section]: { ...prev[section], [key]: arr },
        }));
    };

    const handleCreateDraft = async () => {
        if (!isEmployer) {
            setJobError("Only employers can create jobs");
            return;
        }

        if (!form.title || !form.location || !form.type || !form.openings || !form.description) {
            setJobError("Please fill required fields in Step 1.");
            return;
        }

        try {
            setCreating(true);
            setJobError("");
            setJobSuccess("");

            const payload = {
                ...form,
                openings: Number(form.openings),
                validityDays: Number(form.validityDays),
                requirements,
            };

            await createJobPosting(payload, token); // creates DRAFT [file:6]
            setJobSuccess("Job created as draft!");
            setTab("manage");
            setStep(1);
            setForm(initialForm);
            setRequirements(initialRequirements);
            await loadJobs();
        } catch (e) {
            setJobError("Failed to create draft job");
        } finally {
            setCreating(false);
        }
    };


    const typeLabel = (t) => ({
        FULL_TIME: "Full-time",
        PART_TIME: "Part-time",
        CONTRACT: "Contract",
        INTERNSHIP: "Internship",
    }[t] || t);

    const modeLabel = (m) => ({
        ONSITE: "Onsite",
        REMOTE: "Remote",
        HYBRID: "Hybrid",
    }[m] || m);

    return (
        <>
            <Header />

            <div className="jpm-container">
                <div className="jpm-top">
                    <div>
                        <h1 className="jpm-title">Job Posting Management</h1>
                        <p className="jpm-subtitle">Create and manage your job openings</p>
                    </div>

                    <button className="jpm-back" onClick={() => navigate("/employer/dashboard")}>
                        Back to Dashboard
                    </button>
                </div>

                <div className="jpm-tabs">
                    <button
                        className={`jpm-tab ${tab === "manage" ? "active" : ""}`}
                        onClick={() => setTab("manage")}
                    >
                        Manage Jobs
                    </button>
                    <button
                        className={`jpm-tab ${tab === "create" ? "active" : ""}`}
                        onClick={() => setTab("create")}
                    >
                        Create New Job
                    </button>
                </div>

                {jobError && <div className="jpm-alert error">{jobError}</div>}
                {jobSuccess && <div className="jpm-alert success">{jobSuccess}</div>}

                {tab === "manage" && (
                    <>
                        <div className="jpm-stats">
                            <div className="jpm-stat">
                                <div className="label">Total Jobs</div>
                                <div className="value">{stats.total}</div>
                            </div>
                            <div className="jpm-stat">
                                <div className="label">Active</div>
                                <div className="value green">{stats.active}</div>
                            </div>
                            <div className="jpm-stat">
                                <div className="label">Closed</div>
                                <div className="value">{stats.closed}</div>
                            </div>
                            <div className="jpm-stat">
                                <div className="label">Expired</div>
                                <div className="value orange">{stats.expired}</div>
                            </div>
                        </div>

                        <div className="jpm-filters">
                            <div className="jpm-search">
                                <input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Search by job title or department..."
                                />
                            </div>

                            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                <option value="ALL">All Status</option>
                                <option value="DRAFT">Draft</option>
                                <option value="PUBLISHED">Active</option>
                                <option value="CLOSED">Closed</option>
                                <option value="EXPIRED">Expired</option>
                                <option value="ARCHIVED">Archived</option>
                            </select>

                            <select
                                value={departmentFilter}
                                onChange={(e) => setDepartmentFilter(e.target.value)}
                            >
                                {departments.map((d) => (
                                    <option key={d} value={d}>{d}</option>
                                ))}
                            </select>
                        </div>

                        {loadingJobs ? (
                            <div className="jpm-empty">Loading jobs...</div>
                        ) : filteredJobs.length === 0 ? (
                            <div className="jpm-empty">No jobs found</div>
                        ) : (
                            <div className="jpm-cards">
                                {filteredJobs.map((job) => {
                                    const jobId = job.id || job._id;

                                    const isDraft = job.status === "DRAFT";
                                    const isPublished = job.status === "PUBLISHED";
                                    const isClosed = job.status === "CLOSED";
                                    const isExpired = job.status === "EXPIRED";

                                    return (
                                        <div key={jobId} className="jpm-card">
                                            <div className="jpm-card-left">
                                                <div className="jpm-card-title-row">
                                                    <h3 className="jpm-card-title">{job.title}</h3>
                                                    <span className={`jpm-badge ${job.status?.toLowerCase()}`}>
                                                        {job.status === "PUBLISHED" ? "Active" : job.status}
                                                    </span>
                                                </div>

                                                <div className="jpm-card-meta">{job.department || "—"}</div>

                                                <div className="jpm-card-icons">
                                                    <span>{job.locationType || "Onsite"}</span>
                                                    <span>{job.type}</span>
                                                    <span>{job.openings} openings</span>
                                                    <span>{job._count?.applications ?? 0} applicants</span>
                                                </div>

                                                <div className="jpm-card-dates">
                                                    <span>Posted: {job.createdAt ? new Date(job.createdAt).toLocaleDateString() : "—"}</span>
                                                    <span>Expires: {job.expiryDate ? new Date(job.expiryDate).toLocaleDateString() : "—"}</span>
                                                </div>
                                            </div>

                                            <div className="jpm-card-actions">
                                                <button
                                                    className="btn-outline-job"
                                                    onClick={() => navigate(`/jobs/${jobId}/applications`)}
                                                    disabled={!jobId}
                                                >
                                                    View Applications
                                                </button>
                                                <button
                                                    className="btn-outline-job"
                                                    onClick={() => handleViewJob(jobId)}
                                                    disabled={!jobId}
                                                >
                                                    View Job
                                                </button>


                                                {isDraft && (
                                                    <>
                                                        <button className="btn-outline-job" onClick={() => setEditJob(job)}>
                                                            Edit
                                                        </button>
                                                        <button className="btn-publish" onClick={() => handlePublish(jobId)}>
                                                            Publish
                                                        </button>
                                                    </>
                                                )}

                                                {isPublished && (
                                                    <button className="btn-outline-job" onClick={() => handleClose(jobId)}>
                                                        Close
                                                    </button>
                                                )}

                                                {(isClosed || isExpired) && (
                                                    <button className="btn-danger" onClick={() => handleArchive(jobId)}>
                                                        Archive
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {editJob && (
                            <EditJobModal
                                job={editJob}
                                open={!!editJob}
                                onClose={() => setEditJob(null)}
                                onSave={handleEditSave}
                            />
                        )}
                        <ViewJobModal
                            open={viewOpen}
                            job={viewJob}
                            loading={viewLoading}
                            onClose={() => {
                                setViewOpen(false);
                                setViewJob(null);
                            }}
                            typeLabel={typeLabel}
                            modeLabel={modeLabel}
                        />
                    </>
                )}

                {tab === "create" && (
                    <div className="jpm-wizard">
                        <div className="jpm-wizard-head">
                            <div>
                                <h2>Create New Job Posting</h2>
                                <p>Step {step} of 3</p>
                            </div>

                            <div className="jpm-steps">
                                <div className={`dot ${step >= 1 ? "active" : ""}`} />
                                <div className={`dot ${step >= 2 ? "active" : ""}`} />
                                <div className={`dot ${step >= 3 ? "active" : ""}`} />
                            </div>
                        </div>

                        {step === 1 && (
                            <div className="jpm-panel">
                                <div className="grid">
                                    <label>
                                        Job Title *
                                        <input name="title" value={form.title} onChange={handleFormChange} placeholder="e.g., Senior Software Engineer" />
                                    </label>

                                    <label>
                                        Department/Function
                                        <input name="department" value={form.department} onChange={handleFormChange} placeholder="Select department" />
                                    </label>

                                    <label>
                                        Location *
                                        <input name="location" value={form.location} onChange={handleFormChange} placeholder="Select location" />
                                    </label>

                                    <label>
                                        Employment Type *
                                        <select name="type" value={form.type} onChange={handleFormChange}>
                                            <option value="FULL_TIME">Full-time</option>
                                            <option value="PART_TIME">Part-time</option>
                                            <option value="CONTRACT">Contract</option>
                                            <option value="INTERNSHIP">Internship</option>
                                        </select>

                                    </label>

                                    <label>
                                        Work Mode
                                        <select name="locationType" value={form.locationType} onChange={handleFormChange}>
                                            <option value="ONSITE">Onsite</option>
                                            <option value="REMOTE">Remote</option>
                                            <option value="HYBRID">Hybrid</option>
                                        </select>
                                    </label>

                                    <label>
                                        Number of Openings *
                                        <input name="openings" type="number" min={1} value={form.openings} onChange={handleFormChange} />
                                    </label>
                                </div>

                                <label>
                                    Job Description *
                                    <textarea
                                        name="description"
                                        value={form.description}
                                        onChange={handleFormChange}
                                        placeholder="Describe the role, key responsibilities, qualifications, and expected outcomes..."
                                    />
                                </label>

                                <div className="jpm-actions">
                                    <button className="btn-outline-job" onClick={() => navigate("/employer/dashboard")}>
                                        Back
                                    </button>
                                    <button className="btn-next" onClick={() => setStep(2)}>
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}

                        {step === 2 && (
                            <div className="jpm-panel">
                                <div className="grid">
                                    <label>
                                        Minimum Education Level *
                                        <input
                                            value={requirements.education.minimum}
                                            onChange={(e) => handleReqChange("education", "minimum", e.target.value)}
                                            placeholder="Select education level"
                                        />
                                    </label>

                                    <label>
                                        Years of Experience *
                                        <input
                                            type="number"
                                            min={0}
                                            value={requirements.experience.minimumYears}
                                            onChange={(e) => handleReqChange("experience", "minimumYears", Number(e.target.value))}
                                        />
                                    </label>
                                </div>

                                <label>
                                    Required Certifications/Licenses (comma separated)
                                    <input
                                        value={requirements.certifications.mandatory.join(", ")}
                                        onChange={(e) => handleReqArrChange("certifications", "mandatory", e.target.value)}
                                        placeholder="e.g., PMP, AWS Certified"
                                    />
                                </label>

                                <label>
                                    Required Technical Skills (comma separated)
                                    <input
                                        value={requirements.skills.mandatory.join(", ")}
                                        onChange={(e) => handleReqArrChange("skills", "mandatory", e.target.value)}
                                        placeholder="e.g., React, Node.js, PostgreSQL"
                                    />
                                </label>

                                <label>
                                    Preferred Qualifications (Optional)
                                    <input
                                        value={requirements.other}
                                        onChange={(e) => setRequirements(prev => ({ ...prev, other: e.target.value }))}
                                        placeholder="e.g., MBA, Industry experience"
                                    />
                                </label>

                                <div className="jpm-actions">
                                    <button className="btn-outline-job" onClick={() => setStep(1)}>
                                        Back
                                    </button>
                                    <button className="btn-next" onClick={() => setStep(3)}>
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}

                        {step === 3 && (
                            <div className="jpm-panel">
                                <div className="review">
                                    <h3>Review Job Details</h3>
                                    <div className="review-box">
                                        <div><strong>Title:</strong> {form.title || "—"}</div>
                                        <div><strong>Department:</strong> {form.department || "—"}</div>
                                        <div><strong>Location:</strong> {modeLabel(form.locationType)} • {typeLabel(form.type)}</div>
                                        <div><strong>Openings:</strong> {form.openings}</div>
                                        <div><strong>Min. Education:</strong> {requirements.education.minimum || "—"}</div>
                                        <div><strong>Experience:</strong> {requirements.experience.minimumYears} years</div>
                                    </div>
                                </div>

                                <div className="publish-options">
                                    <h3>Publishing Options</h3>

                                    <label className="checkrow">
                                        <input
                                            type="checkbox"
                                            checked={publishingOptions.internalPortal}
                                            onChange={(e) =>
                                                setPublishingOptions(prev => ({ ...prev, internalPortal: e.target.checked }))
                                            }
                                        />
                                        <div>
                                            <div className="checktitle">Company Internal Career Portal</div>
                                            <div className="checksub">Visible to current employees and internal referrals</div>
                                        </div>
                                    </label>

                                    <label className="checkrow">
                                        <input
                                            type="checkbox"
                                            checked={publishingOptions.publicBoard}
                                            onChange={(e) =>
                                                setPublishingOptions(prev => ({ ...prev, publicBoard: e.target.checked }))
                                            }
                                        />
                                        <div>
                                            <div className="checktitle">Platform’s Public Job Board</div>
                                            <div className="checksub">Visible to all job seekers on the platform</div>
                                        </div>
                                    </label>

                                    <label>
                                        Validity Period (Days)
                                        <select
                                            name="validityDays"
                                            value={form.validityDays}
                                            onChange={handleFormChange}
                                        >
                                            <option value={7}>7 days</option>
                                            <option value={15}>15 days</option>
                                            <option value={30}>30 days</option>
                                            <option value={60}>60 days</option>
                                        </select>
                                        <div className="hint">The job posting will automatically expire after this period unless renewed.</div>
                                    </label>

                                    <div className="note">
                                        Note: Once published, the job will be visible according to your selected options. You can manually close the posting at any time from the job management dashboard.
                                    </div>
                                </div>

                                <div className="jpm-actions">
                                    <button className="btn-outline-job" onClick={() => setStep(2)}>
                                        Back
                                    </button>
                                    <button className="btn-next" onClick={handleCreateDraft} disabled={creating}>
                                        {creating ? "Creating..." : "Create Job"}
                                    </button>

                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </>
    );
};

export default JobPostingManagement;
